// src/pages/LatestBrowserUpdates.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
  Row,
  Col,
  Button as RSButton, // reactstrap Button aliased
} from "reactstrap";
import { format } from "date-fns";
import { request } from "../utils/request";

import {
  IconButton,
  Box,
  Breadcrumbs,
  Link as MUILink,
  Checkbox,
  Menu,
  MenuItem,
  Typography,
  Stack,
  Button as MUIButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export default function LatestBrowserUpdates() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 25 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ version: "", link: "", notes: "", isLatest: false });

  // selection & menu state
  const [selectedIds, setSelectedIds] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const fetchPage = useCallback(async (page = 1, limit = 25) => {
    setLoading(true);
    try {
      const resp = await request(`browser-updates?page=${page}&limit=${limit}`, { method: "GET" });
      if (resp && resp.success && Array.isArray(resp.data)) {
        setItems(resp.data);
        setMeta(resp.meta || { total: resp.data.length, page, limit });
      } else if (Array.isArray(resp)) {
        setItems(resp);
        setMeta({ total: resp.length, page, limit });
      } else {
        const arr = resp && resp.data ? resp.data : [];
        setItems(Array.isArray(arr) ? arr : []);
        setMeta({ total: Array.isArray(arr) ? arr.length : 0, page, limit });
      }
    } catch (err) {
      console.error("Failed to fetch browser-updates:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, 25);
  }, [fetchPage]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ version: "", link: "", notes: "", isLatest: false });
    setModalOpen(true);
  };

  const openEdit = (it) => {
    setEditItem(it);
    setForm({
      version: it.version || "",
      link: it.link || "",
      notes: it.notes || "",
      isLatest: !!it.isLatest
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.version || !form.link) {
      alert("Version and link are required");
      return;
    }
    setLoading(true);
    try {
      if (editItem && editItem._id) {
        await request(`browser-updates/${editItem._id}`, { method: "PUT", body: form });
      } else {
        await request("browser-updates", { method: "POST", body: form });
      }
      setModalOpen(false);
      await fetchPage(1, meta.limit);
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm("Delete this update?")) return;
    setLoading(true);
    try {
      await request(`browser-updates/${it._id}`, { method: "DELETE" });
      setSelectedIds((prev) => prev.filter((id) => id !== it._id));
      await fetchPage(1, meta.limit);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleLatest = async (it) => {
    setLoading(true);
    try {
      await request(`browser-updates/${it._id}`, { method: "PUT", body: { isLatest: true } });
      await fetchPage(1, meta.limit);
    } catch (err) {
      console.error("Failed to set latest:", err);
      alert("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return format(new Date(iso), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  // selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = items.length > 0 && selectedIds.length === items.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(items.map((it) => it._id));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleEditSelected = () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length > 1) {
      alert("Please select only one item to edit.");
      return;
    }
    const it = items.find(i => i._id === selectedIds[0]);
    if (it) {
      openEdit(it);
      // optional: keep selection or clear
      // clearSelection();
    } else {
      alert("Selected item not found.");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected update(s)?`)) return;
    setLoading(true);
    try {
      await Promise.all(selectedIds.map(id => request(`browser-updates/${id}`, { method: "DELETE" })));
      await fetchPage(1, meta.limit);
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete failed:", err);
      alert("Bulk delete failed");
    } finally {
      setLoading(false);
    }
  };

  // per-row three-dot menu
  const handleOpenRowMenu = (e, it) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(it._id);
  };
  const handleCloseRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };
  const handleRowEdit = () => {
    const it = items.find(i => i._id === menuRowId);
    if (it) openEdit(it);
    handleCloseRowMenu();
  };
  const handleRowDelete = async () => {
    const it = items.find(i => i._id === menuRowId);
    if (it) await handleDelete(it);
    handleCloseRowMenu();
  };

  return (
    <div className="widget-card">
      <Card>
        <CardBody>
          <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
                <MUILink
                  href="/dashboard"
                  underline="hover"
                  sx={{
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    color: '#2d9cdb',
                    '&:hover': {
                      color: '#0b1d51',
                    },
                  }}
                >
                  Dashboard
                </MUILink>

                <Typography sx={{ fontSize: '0.95rem', color: 'black' }}>
                  Browser Updates
                </Typography>
              </Breadcrumbs>

              <MUIButton
                size="small"
                variant="contained"
                onClick={openCreate}
                sx={{
                  backgroundColor: '#2d9cdb',
                  '&:hover': { backgroundColor: '#0b1d51', color: '#fff' },
                  px: 2,
                  py: 0.8,
                  textTransform: 'none',
                }}
              >
                Add update
              </MUIButton>
            </Stack>
          </Box>

          {/* toolbar shown when rows selected */}
          {selectedIds.length > 0 && (
            <Box mb={1} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">{selectedIds.length} selected</Typography>

              <MUIButton
                size="small"
                variant="outlined"
                onClick={handleEditSelected}
                disabled={selectedIds.length !== 1}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </MUIButton>

              <MUIButton
                size="small"
                variant="contained"
                onClick={handleDeleteSelected}
                sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
              >
                Delete
              </MUIButton>

              <MUIButton
                size="small"
                onClick={clearSelection}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </MUIButton>
            </Box>
          )}

          <div style={{ minHeight: 240 }}>
            {loading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: 160 }}>
                <Spinner size="sm" color="primary" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center text-muted" style={{ padding: 40 }}>No updates found</div>
            ) : (
              // scrollable container with sticky header
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <Table responsive size="sm" bordered hover>
                  <thead style={{ position: 'sticky', top: 0, background: '#f5f7fb', zIndex: 2 }}>
                    <tr>
                      <th style={{ width: 56, textAlign: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={isAllSelected}
                          indeterminate={selectedIds.length > 0 && !isAllSelected}
                          onChange={toggleSelectAll}
                          inputProps={{ 'aria-label': 'select all updates' }}
                        />
                      </th>
                      <th>Version</th>
                      <th>Link</th>
                      <th>Notes</th>
                      <th>Latest</th>
                      <th>Created At</th>
                      <th style={{ width: 96, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it._id}>
                        <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                          <Checkbox
                            size="small"
                            checked={selectedIds.includes(it._id)}
                            onChange={() => toggleSelect(it._id)}
                            inputProps={{ "aria-label": `select-${it._id}` }}
                          />
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{it.version}</td>
                        <td style={{ verticalAlign: "middle", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                          <a href={it.link} target="_blank" rel="noreferrer">{it.link}</a>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{it.notes || "—"}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          {it.isLatest ? <span className="badge bg-success">Latest</span> : (
                            <RSButton size="sm" color="link" onClick={() => toggleLatest(it)}>Make latest</RSButton>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{fmt(it.createdAt)}</td>
                        <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                          <IconButton
                            size="small"
                            aria-controls={menuRowId === it._id ? `row-menu-${it._id}` : undefined}
                            aria-haspopup="true"
                            onClick={(e) => handleOpenRowMenu(e, it)}
                            sx={{ p: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* three-dot Menu (text-only items) */}
      <Menu
        id={menuRowId ? `row-menu-${menuRowId}` : undefined}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(menuRowId)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleRowEdit}>Edit</MenuItem>
        <MenuItem onClick={handleRowDelete}>Delete</MenuItem>
      </Menu>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editItem ? "Edit Browser Update" : "Add Browser Update"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Version</Label>
            <Input value={form.version} onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))} />
          </FormGroup>
          <FormGroup>
            <Label>Download / Release Link</Label>
            <Input value={form.link} onChange={(e) => setForm(f => ({ ...f, link: e.target.value }))} />
          </FormGroup>
          <FormGroup>
            <Label>Notes (optional)</Label>
            <Input type="textarea" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={!!form.isLatest} onChange={(e) => setForm(f => ({ ...f, isLatest: e.target.checked }))} />{' '}
              Mark as latest
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <RSButton color="secondary" onClick={() => setModalOpen(false)}>Cancel</RSButton>
          <RSButton color="primary" onClick={submit} disabled={loading}>{loading ? "Saving..." : "Save"}</RSButton>
        </ModalFooter>
      </Modal>
    </div>
  );
}
